import { FC, useState, useCallback } from 'react';
import { X, Plus } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface SpecialtyTagsProps {
  specialties: string[];
  onChange: (specialties: string[]) => void;
  disabled?: boolean;
}

export const SpecialtyTags: FC<SpecialtyTagsProps> = ({
  specialties,
  onChange,
  disabled,
}) => {
  const [newTag, setNewTag] = useState('');

  const handleAddTag = useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !specialties.includes(trimmed)) {
      onChange([...specialties, trimmed]);
      setNewTag('');
    }
  }, [newTag, specialties, onChange]);

  const handleRemoveTag = useCallback(
    (index: number) => {
      onChange(specialties.filter((_, i) => i !== index));
    },
    [specialties, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {specialties.map((specialty, index) => (
          <span
            key={`${specialty}-${index}`}
            className={cn(
              'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm',
              'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
            )}
          >
            {specialty}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemoveTag(index)}
                className="hover:text-blue-600 dark:hover:text-blue-100 focus:outline-none"
                aria-label={`Remove ${specialty}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add specialty..."
            className={cn(
              'flex-1 px-3 py-2 text-sm rounded-md border',
              'bg-white dark:bg-slate-700',
              'text-gray-900 dark:text-slate-100',
              'border-gray-300 dark:border-slate-600',
              'placeholder-gray-400 dark:placeholder-slate-500',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={!newTag.trim()}
            className={cn(
              'px-3 py-2 rounded-md text-sm font-medium',
              'bg-blue-600 text-white hover:bg-blue-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
